 
import axios, { type AxiosPromise, type AxiosRequestConfig } from "axios";
import { type FileTypes, bytesToMb, checkFileType, fileExtensionMap } from "./fileutils";

export interface UploadFilesConfig {
  onUploadProgress?: (e: ProgressEvent) => void
  timeout?: number
  fileType: Exclude<FileTypes, "unknown">
  queryParams?: any
  overrideMaxFileLength?: number
}

// Returns the type of the first argument of a function, if that argument implements IUploadMultipleFilesInstruction
// E.g. from `typeof window.CoreApi.File.UploadAdvancingFiles` it would return `server.dto.UploadMultipleAdvancingFileInstruction`
export type ExtractFilesUploadInstruction<T> = T extends ((arg: infer U, ...args) => any) ?
  (U extends server.dto.IUploadMultipleFilesInstruction ? U : never)
  : never;

// Returns the type of the first argument of a function, if that argument implements IUploadSingleFileInstruction
export type ExtractFileUploadInstruction<T> = T extends ((arg: infer U, ...args) => any) ?
  (U extends server.dto.IUploadSingleFileInstruction ? U : never)
  : never;

export interface MultipleFilesApi {
  (instruction: server.dto.IUploadMultipleFilesInstruction, ...args: any[]): AxiosPromise<any>
  absoluteUrl: (...args) => string
  requestSizeLimit: number
};

export interface SingleFileApi {
  (instruction: server.dto.IUploadSingleFileInstruction, ...args: any[]): AxiosPromise<any>
  absoluteUrl: (...args) => string
  requestSizeLimit: number
};

type Api = SingleFileApi | MultipleFilesApi;

export const uploadFilesToCoreApi = <
  TApi extends MultipleFilesApi
>(
  api: TApi,
  endpoint: string,
  instruction: Omit<ExtractFilesUploadInstruction<TApi>, "files"> & { files: File[] },
  config: UploadFilesConfig
) => {
  const { files, ...restOfTheInstruction } = instruction;

  const formData = new FormData();
  instruction.files.forEach(file => { formData.append("files", file); });

  return uploadFilesToCoreApiBase(api, endpoint, files, restOfTheInstruction, formData, config);
};

export const uploadFileToCoreApi = <
  TApi extends SingleFileApi,
>
(
  api: TApi,
  endpoint: string,
  instruction: Omit<ExtractFileUploadInstruction<TApi>, "file"> & { file: File },
  config: UploadFilesConfig
) => {
  const { file, ...restOfTheInstruction } = instruction;
  const formData = new FormData();
  formData.append("file", file);
  return uploadFilesToCoreApiBase(api, endpoint, [file], restOfTheInstruction, formData, config);
};

const uploadFilesToCoreApiBase = (api: Api, endpoint: string, files: File[], otherInstructionParams: any, formData: FormData, config: UploadFilesConfig) => {
  const totalFileSize = files.reduce((acc, file) => acc + file.size, 0);

  const fileLengthValidationResult = validateFileLength(totalFileSize, config.overrideMaxFileLength ?? api.requestSizeLimit);
  if (fileLengthValidationResult !== true) {
    return fileLengthValidationResult;
  }

  const fileExtensionValidationResult = validateExtension(files, config.fileType);
  if (fileExtensionValidationResult !== true) {
    return fileExtensionValidationResult;
  }

  Object.keys(otherInstructionParams).forEach((key) => {
    if (Array.isArray(otherInstructionParams[key])) {
      otherInstructionParams[key].forEach((item) => {
        formData.append(key, item);
      });
      return;
    }
    formData.append(key, otherInstructionParams[key]);
  });

  const axiosConfig: AxiosRequestConfig = {
    onUploadProgress: config?.onUploadProgress,
    timeout: config?.timeout ?? (120 * 1000),
    params: config?.queryParams,
    withCredentials: true
  };

  return axios.post(endpoint, formData, axiosConfig);
};

export const validateFileLength = (size: number, maxSize: number): true | string => {
  if (size >= maxSize) {
    return RESX.GeneralWarnings.MaxAttachedFiles.replace("{0}", bytesToMb(maxSize).toString());
  }

  if (size === 0) {
    return RESX.GeneralWarnings.TheFileContainsNoData;
  }

  return true;
};

export const validateExtension = (files: Array<File | server.dto.File>, validExtensions: FileTypes): true | string => {
  if (!files.every(file => checkFileType(file, validExtensions))) {
    const fileExtensions = fileExtensionMap[validExtensions];
    return RESX.GeneralWarnings.CanOnlyUploadFileExtensions.replace("{0}", fileExtensions);
  }

  return true;
};